import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Menu, MenuItem, ListItemText, ListItemIcon, useMediaQuery, useTheme } from '@mui/material';
import LanguageIcon from '@mui/icons-material/Language';
import { styled } from '@mui/material/styles';

const languages = [
  {
    code: 'en',
    name: 'English',
    nativeName: 'English',
    flag: '🇺🇸',
    dir: 'ltr'
  },
  {
    code: 'dr',
    name: 'Dari',
    nativeName: 'دری',
    flag: '🇦🇫',
    dir: 'rtl'
  },
  {
    code: 'ps',
    name: 'Pashto',
    nativeName: 'پښتو',
    flag: '🇦🇫',
    dir: 'rtl'
  }
];

const LanguageButton = styled(Button)(({ theme }) => ({
  height: 36,
  padding: '0 12px',
  borderRadius: 24,
  border: `1px solid ${theme.palette.divider}`,
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
  },
  [theme.breakpoints.down('sm')]: {
    padding: '0 8px',
    height: 32,
    minWidth: 'auto',
  },
  [theme.breakpoints.down('xs')]: {
    padding: '0 6px',
    height: 30,
  }
}));

const LanguageSelector = () => {
  const { i18n } = useTranslation();
  const [selected, setSelected] = useState(i18n.language?.substring(0, 2) || 'en');
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isXsMobile = useMediaQuery('(max-width:500px)');

  // Update HTML lang and dir attributes when language changes
  useEffect(() => {
    const currentLang = languages.find(lang => lang.code === selected) || languages[0];
    document.documentElement.lang = currentLang.code;
    document.documentElement.dir = currentLang.dir;
    
    // Remove existing direction class and add the new one
    document.body.classList.remove('ltr', 'rtl');
    document.body.classList.add(currentLang.dir);
  }, [selected]);

  // Initial language setup
  useEffect(() => {
    const savedLang = localStorage.getItem('i18nextLng');
    if (savedLang && savedLang.length > 1) {
      const langCode = savedLang.substring(0, 2);
      const foundLang = languages.find(lang => lang.code === langCode);
      if (foundLang) {
        setSelected(langCode);
        i18n.changeLanguage(langCode);
      }
    }
  }, [i18n]);

  // Find current language
  const currentLang = languages.find(lang => lang.code === selected) || languages[0];

  // Change to specific language
  const changeLanguage = (langCode) => {
    i18n.changeLanguage(langCode);
    setSelected(langCode);
    localStorage.setItem('i18nextLng', langCode);
    handleClose();
  };

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const isRTL = currentLang.dir === 'rtl';

  return (
    <>
      <LanguageButton
        variant="outlined"
        onClick={handleClick}
        title={`Current language: ${currentLang.name}`}
        size="small"
        sx={{ 
          borderColor: isMobile ? 'transparent' : undefined,
          backgroundColor: isMobile ? 'transparent' : undefined,
          '&:hover': {
            borderColor: isMobile ? 'transparent' : undefined,
            backgroundColor: isMobile ? 'rgba(0, 0, 0, 0.04)' : undefined,
          }
        }}
      >
        <LanguageIcon fontSize={isMobile ? 'small' : 'small'} />
        {!isXsMobile && (
          <span 
            style={{ 
              fontFamily: currentLang.dir === 'rtl' ? 'dari' : 'inherit',
              fontSize: isMobile ? '0.75rem' : 'inherit'
            }}
          >
            {currentLang.nativeName}
          </span>
        )}
      </LanguageButton>
      
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: isRTL ? 'left' : 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: isRTL ? 'left' : 'right',
        }}
      >
        {languages.map((language) => (
          <MenuItem 
            key={language.code}
            onClick={() => changeLanguage(language.code)}
            selected={selected === language.code}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              fontFamily: language.dir === 'rtl' ? 'dari' : 'inherit',
            }}
          >
            <ListItemIcon sx={{ minWidth: 'auto', fontSize: '1rem' }}>
              {language.flag}
            </ListItemIcon>
            <ListItemText primary={language.nativeName} />
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

export default LanguageSelector;
